import '../../../instrument';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, effect, input } from '@angular/core';
import { FunctionComponent, createElement, Suspense, ReactElement, isValidElement } from 'react';
import { createRoot, Root } from 'react-dom/client';
import { Apollo } from 'apollo-angular';
import { joyTheme, materialTheme } from '../../theme';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';
import { FindIdentityProvider } from '@startuptools/business-ui/identity/add/FindIdentityDialog';
import { type ApolloClient, ApolloProvider, type InMemoryCache } from '@apollo/client';
import { ThemeProvider } from '@emotion/react';
import { I18nProvider } from '@lingui/react';
import { JwtProvider } from './UserIdContext';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { CssVarsProvider as JoyCssVarsProvider, THEME_ID as JOY_THEME_ID } from '@mui/joy/styles';
import { CompanyIdProvider } from '@startuptools/common/react-wrapper';
import { reactErrorHandler } from '@sentry/react';
import { loadI18n } from '../../i18n';

@Component({
  standalone: true,
  selector: 'app-react-wrapper',
  template: '',
  styles: `
    :host {
      display: block;
      max-width: '0px';
    }

    :host.display-none {
      display: none;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReactWrapperComponent<Props extends object> implements AfterViewInit, OnDestroy {
  component = input.required<FunctionComponent<Props>>();
  apolloClient = input<ApolloClient<InMemoryCache>>(this.apollo.client);
  props = input<Props>();
  userId = input<string>();
  companyId = input<string>();

  private root: Root | undefined;

  constructor(
    private elementRef: ElementRef,
    private apollo: Apollo,
  ) {
    effect(() => {
      this.render(this.component(), this.apolloClient(), this.companyId(), this.props());
    });
  }

  render(
    component: FunctionComponent<Props> | ReactElement,
    apolloClient: ApolloClient<InMemoryCache>,
    companyId?: string | null,
    props?: Props,
  ) {
    if (!this.root) {
      return;
    }

    const i18n = window.location.pathname.startsWith('/sv') ? loadI18n('sv') : loadI18n('en');
    const children = isValidElement(component) ? component : createElement(component, props);
    this.root.render(
      <I18nProvider i18n={i18n}>
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.locale}>
          <ApolloProvider client={apolloClient}>
            <ThemeProvider theme={materialTheme}>
              <JoyCssVarsProvider theme={{ [JOY_THEME_ID]: joyTheme }}>
                <NuqsAdapter>
                  <JwtProvider>
                    <CompanyIdProvider companyId={companyId}>
                      <FindIdentityProvider>
                        <Suspense>
                          <ErrorBoundary>{children}</ErrorBoundary>
                        </Suspense>
                      </FindIdentityProvider>
                    </CompanyIdProvider>
                  </JwtProvider>
                </NuqsAdapter>
              </JoyCssVarsProvider>
            </ThemeProvider>
          </ApolloProvider>
        </LocalizationProvider>
      </I18nProvider>,
    );
  }

  ngAfterViewInit(): void {
    this.root = createRoot(this.elementRef.nativeElement, {
      onUncaughtError: reactErrorHandler((error, errorInfo) => {
        // eslint-disable-next-line no-console
        console.warn('Uncaught error', error, errorInfo.componentStack);
      }),
      onCaughtError: reactErrorHandler(),
      onRecoverableError: reactErrorHandler(),
    });

    this.render(this.component(), this.apolloClient(), this.companyId(), this.props());
  }

  ngOnDestroy(): void {
    this.root?.unmount();
    this.root = undefined;
  }
}
